import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  styled,
} from '@mui/material';
import React, { useState } from 'react';

const FaqWrapper = styled(Box)({
  padding: '96px 0',
  background: '#FFFFFF',
  maxWidth: '100%',
});

const FaqAccordion = styled(Accordion)(({ theme }) => ({
  borderTop: '1px solid #E9EAEB',
  boxShadow: 'none',
  borderRadius: '0 !important',
  marginBottom: '0',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: 0,
  },
}));

const FaqAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  padding: '24px 0',
  '& .MuiAccordionSummary-content': {
    margin: '0',
  },
}));

const ContactCard = styled(Box)(({ theme }) => ({
  position: 'relative',
  border: '1px solid rgba(157, 21, 0, 0.2)',
  borderRadius: '16px',
  padding: '32px 32px 40px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '32px',
  marginTop: '32px',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundImage: 'url("/images/pricing/questions-bg.jpg")',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    opacity: 0.1,
    borderRadius: '16px',
  },
}));

const AvatarGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
}));

const Avatar = styled(Box)(({ theme }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '200px',
  border: '1.5px solid #FFFFFF',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundColor: '#c6d0cb',
  margin: '0 -8px',
}));

interface FaqItem {
  question: string;
  answer: string;
  expanded?: boolean;
}

const faqItems: FaqItem[] = [
  {
    question: 'Is there a free trial available?',
    answer:
      "Yes, you can try us for free for 31 days. If you want, we'll provide you with a free, personalized 30-minute onboarding call to get you up and running as soon as possible.",
    expanded: true,
  },
  {
    question: 'Is Terr.AI free for my tenants?',
    answer:
      'Yes, Terr.AI is free for your tenants. They can continue using traditional call, text, and email.  They would pay a small transaction processing fee if they are making rental payments through our platform.',
  },
  {
    question: 'How much control do I have over AI-Powered Communications?',
    answer:
      'You have full control over the AI-Powered Communications. No response would be sent to your tenants without your approval.',
  },
  {
    question: 'Is my information secure?',
    answer:
      'Yes, your information is secure. We use industry-standard encryption to protect your data. Please refer to our Privacy Policy for more information.',
  },
  {
    question: 'Do you sell my data to third parties?',
    answer:
      'No, we do not sell your data to third parties. We only use your data to provide you with the best possible service.',
  },
  {
    question: 'How easy is offboarding?',
    answer:
      'Offboarding is easy. We always want you to be happy with our service. If you ever need to leave, we will help you with the process.',
  },
];

const FaqSection: React.FC = () => {
  const [expanded, setExpanded] = useState<string | false>(faqItems[0].question);

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <FaqWrapper>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="landingH2"
            component="h2"
            sx={{
              mb: 2,
              color: '#181D27',
              fontFamily: 'Nohemi',
              fontSize: { xs: '28px', md: '36px' },
              fontWeight: 600,
              lineHeight: 1.2,
            }}
          >
            Frequently asked questions
          </Typography>
          <Typography
            variant="landingBody"
            sx={{
              color: '#535862',
              maxWidth: '800px',
              mx: 'auto',
              fontSize: { xs: '16px', md: '20px' },
              fontFamily: 'Inter',
              fontWeight: 400,
              lineHeight: 1.5,
            }}
          >
            Everything you need to know about the Terra.AI and how to join.
          </Typography>
        </Box>

        <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
          {faqItems.map(item => (
            <FaqAccordion
              key={item.question}
              expanded={expanded === item.question}
              onChange={handleChange(item.question)}
              disableGutters
            >
              <FaqAccordionSummary
                aria-controls={`panel${item.question}-content`}
                id={`panel${item.question}-header`}
                expandIcon={
                  expanded === item.question ? (
                    <img
                      src="/images/pricing/minus-circle.svg"
                      alt="Collapse"
                      width={24}
                      height={24}
                    />
                  ) : (
                    <img
                      src="/images/pricing/plus-circle.svg"
                      alt="Expand"
                      width={24}
                      height={24}
                    />
                  )
                }
              >
                <Typography
                  sx={{
                    fontSize: '20px',
                    fontWeight: 600,
                    fontFamily: 'Inter',
                    color: '#181D27',
                    lineHeight: 1.4,
                  }}
                >
                  {item.question}
                </Typography>
              </FaqAccordionSummary>
              <AccordionDetails sx={{ pb: 3, pt: 0 }}>
                <Typography
                  sx={{
                    color: '#535862',
                    fontSize: '16px',
                    lineHeight: 1.5,
                    fontFamily: 'Inter',
                    fontWeight: 400,
                  }}
                >
                  {item.answer}
                </Typography>
              </AccordionDetails>
            </FaqAccordion>
          ))}
        </Box>

        <ContactCard>
          <AvatarGroup>
            <Avatar
              sx={{
                backgroundImage: 'url("/images/pricing/avatar-1.jpg")',
                zIndex: 1,
              }}
            />
            <Avatar
              sx={{
                backgroundImage: 'url("/images/pricing/avatar-3.jpg")',
                zIndex: 2,
              }}
            />
            <Avatar
              sx={{
                backgroundImage: 'url("/images/pricing/avatar-2.jpg")',
                zIndex: 1,
              }}
            />
          </AvatarGroup>
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              sx={{
                mb: 1,
                fontFamily: 'Nohemi',
                fontWeight: 600,
                fontSize: '20px',
                lineHeight: 1.4,
                color: '#181D27',
              }}
            >
              Still have questions?
            </Typography>
            <Typography
              sx={{
                color: '#535862',
                fontSize: '16px',
                lineHeight: 1.5,
                fontFamily: 'Inter',
                fontWeight: 400,
              }}
            >
              Can&apos;t find the answer you&apos;re looking for? Please chat to our friendly team.
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            sx={{
              borderRadius: '8px',
              padding: '12px 18px',
              fontFamily: 'Inter',
              fontWeight: 600,
              fontSize: '16px',
              lineHeight: 1.5,
              backgroundColor: '#9D1500',
              boxShadow:
                '0px 1px 2px 0px rgba(16, 24, 40, 0.05), inset 0px -2px 0px 0px rgba(10, 13, 18, 0.05), inset 0px 0px 0px 1px rgba(10, 13, 18, 0.18)',
              '&:hover': {
                backgroundColor: '#8a1300',
              },
            }}
            href="mailto:hello@terr.ai"
          >
            Get in touch
          </Button>
        </ContactCard>
      </Container>
    </FaqWrapper>
  );
};

export default FaqSection;
